import { Dialog } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";

import { OccurrenceImportValidationResponse } from "../../../../../models/occurrenceImport";

interface FileValidationProps {
    show: boolean
    selectedFile: File | null
    validationResponse: OccurrenceImportValidationResponse
    importing: boolean

    onClose: () => void
    handleRetry: () => void
    handleImport: () => Promise<void>
}

export const FileValidation = (props: FileValidationProps) => {
    const intl = useIntl();

    return (
        <Dialog
            show={props.show}
            onClose={props.onClose}
            bsSize="md"
            showCloseButton
            onCloseValidation={() => true}
            title={
                <div className="flex flex-col gap-2">
                    <h2 className="font-semibold text-[16px] m-0">
                        {intl.formatMessage({ id: "occurrence.import.create" })}
                    </h2>

                    <div className="flex items-center gap-3 text-[#697a8b] dark:text-neutral-400 text-[12px]">
                        <span className="max-w-[240px] truncate" title={props.selectedFile?.name}>
                            {props.selectedFile?.name || "-"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#9ad9b0] dark:border-green-800 bg-[#e8f8ee] dark:bg-green-900/30 px-3 py-1 text-[#1e8a49] dark:text-green-300 font-semibold">
                            <span className="rioglyph rioglyph-ok-circle" aria-hidden="true" />
                            {intl.formatMessage({ id: "occurrence.import.validation.validationDone" })}
                        </span>
                    </div>
                </div>
            }
            body={
                <div className="p-2">
                    <div className={`alert alert-dismissible ${!props.validationResponse.valid || props.validationResponse.errors.length > 0 ? "alert-secondary" : "alert-success"} mb-8 p-4`}>
                        <div className="display-flex gap-10">
                            <span className={`${!props.validationResponse.valid || props.validationResponse.errors.length > 0 ? "text-color-secondary" : "text-color-success"} text-size-h4 rioglyph rioglyph-info-sign`} />
                            <div>
                                <strong className={`${!props.validationResponse.valid || props.validationResponse.errors.length > 0 ? "text-color-secondary" : "text-color-success"} text-size-14`}>
                                    {props.validationResponse.errors.length > 0 ?
                                        intl.formatMessage(
                                            { id: "occurrence.import.validation.errorCount" },
                                            { count: props.validationResponse.errors.length, b: (chunks) => <strong>{chunks}</strong> }
                                        ) :
                                        intl.formatMessage({ id: props.validationResponse.valid ? "occurrence.import.validation.genericSuccessMessage" : "occurrence.import.validation.genericErrorMessage" })
                                    }
                                </strong>
                                <div>
                                    {props.validationResponse.errors.length > 0 ?
                                        intl.formatMessage({ id: "occurrence.import.validation.fixAndRetry" })
                                        : props.validationResponse.message
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    {props.validationResponse.valid || props.validationResponse.errors.length > 0 ? (
                        <>
                            <div className="flex items-center gap-4 text-[16px] mb-4">
                                <span>
                                    {intl.formatMessage(
                                        { id: "occurrence.import.validation.totalLines" },
                                        { totalLines: props.validationResponse.totalLines, b: (chunks) => <strong>{chunks}</strong> }
                                    )}
                                </span>
                                <span className="h-6 w-px bg-[#d9e1e8] dark:bg-neutral-700 mx-4" />
                                <span className="text-[#1e8a49] dark:text-green-400">
                                    {intl.formatMessage(
                                        { id: "occurrence.import.validation.validCount" },
                                        { count: props.validationResponse.totalLines - props.validationResponse.errors.length, b: (chunks) => <strong>{chunks}</strong> }
                                    )}
                                </span>
                                <span className="h-6 w-px bg-[#d9e1e8] dark:bg-neutral-700 mx-4" />
                                <span className="text-[#c74a4a] dark:text-red-400">
                                    {intl.formatMessage(
                                        { id: "occurrence.import.validation.errorOnlyCount" },
                                        { count: props.validationResponse.errors.length, b: (chunks) => <strong>{chunks}</strong> }
                                    )}
                                </span>
                            </div>

                            <div className="border border-[#d9e1e8] dark:border-neutral-700 rounded-md overflow-hidden my-8">
                                <table className="table m-0 table-hover">
                                    <thead>
                                        <tr>
                                            <th className="bg-[#f2f5f7] dark:bg-neutral-800 text-[#3b4652] dark:text-neutral-300">
                                                {intl.formatMessage({ id: "occurrence.import.validation.line" })}
                                            </th>
                                            <th className="bg-[#f2f5f7] dark:bg-neutral-800 text-[#3b4652] dark:text-neutral-300">
                                                {intl.formatMessage({ id: "occurrence.import.validation.errorMessage" })}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            props.validationResponse.errors.map((errorItem, index) => (
                                                <tr key={`error-row-${errorItem.row}-${index}`}>
                                                    <td>{errorItem.row}</td>
                                                    <td>
                                                        {errorItem.messages.map((msg, msgIndex) => (
                                                            <div key={`error-msg-${msgIndex}`} className="text-[#c74a4a] dark:text-red-400 mb-2">
                                                                {msg}
                                                            </div>
                                                        ))}
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : null}

                    <div className="flex justify-end gap-3">
                        <button type="button" className="btn btn-default" onClick={props.onClose}>
                            {intl.formatMessage({ id: "occurrence.import.validation.cancel" })}
                        </button>

                        {
                            props.validationResponse.errors.length === 0 && props.validationResponse.valid ? (
                                <button type="button" className="btn btn-primary" disabled={props.importing} onClick={props.handleImport}>
                                    {intl.formatMessage({ id: props.importing ? "occurrence.import.validation.importing" : "occurrence.import.validation.finish" })}
                                </button>
                            ) : (
                                <button type="button" className="btn btn-primary" onClick={props.handleRetry}>
                                    {intl.formatMessage({ id: "occurrence.import.validation.retry" })}
                                </button>
                            )
                        }
                    </div>
                </div>
            }
        />
    );
}